import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../_core/_services/auth.service';
import { ApiResponse } from '../../_core/_model/api-response';
import { RegisterRequest, RegisterResponseData } from '../../_core/_model/users';
import { TokenStorageService } from '../../_core/_services/token-storage.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { ApiService } from '../../_core/_services/api.service';
import { MiniCountryDto } from '../../_core/_model/countries';

type RegisterForm = FormGroup<{
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
  phone: FormControl<string>;
  country: FormControl<string>;
}>;

@Component({
  selector: 'app-register',
  standalone: false,
  templateUrl: './register.html',
  styleUrl: './register.css',
})
export class Register implements OnInit {
  loading = false;
  submitted = false;
  error = '';
  success = '';

  countriesLoading = false;
  countriesError = '';
  countries: MiniCountryDto[] = [];

  form!: RegisterForm;

  constructor(
    private notifyService: NotificationService,
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private tokenStore: TokenStorageService,
    private api: ApiService
  ) {
    this.form = this.fb.nonNullable.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
      phone: [''],
      country: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.loadCountries();
  }

  get selectedCountry(): MiniCountryDto | null {
    const code = this.form.controls.country.value;
    return this.countries.find(country => country.code === code) ?? null;
  }

  get selectedCountryFlag(): string {
    return this.countryFlag(this.selectedCountry?.code ?? '');
  }

  loadCountries(): void {
    this.countriesLoading = true;
    this.countriesError = '';

    this.api.getMain().subscribe({
      next: response => {
        this.countries = [...(response.data?.countries ?? [])]
          .map(country => ({
            ...country,
            code: country.code.trim().toUpperCase(),
            dialCode: country.dialCode?.trim() || null
          }))
          .filter(country => !country.status || country.status.toLowerCase() === 'active')
          .sort((left, right) => left.name.localeCompare(right.name));

        if (!this.countries.length) {
          this.countriesError = 'No countries are currently available for registration.';
          return;
        }

        const currentCountry = this.form.controls.country.value;
        const currentExists = this.countries.some(country => country.code === currentCountry);

        if (!currentExists) {
          const defaultCountry =
            this.countries.find(country => country.code === this.browserCountryCode()) ??
            this.countries.find(country => country.code === 'NG') ??
            this.countries[0];

          if (defaultCountry) {
            this.form.controls.country.setValue(defaultCountry.code);
          }
        }
      },
      error: err => {
        this.countriesLoading = false;
        this.countriesError =
          err?.error?.message ||
          err?.error?.Message ||
          'Unable to load the available countries.';
      },
      complete: () => {
        this.countriesLoading = false;
      }
    });
  }

  onCountryChanged(): void {
    this.form.controls.phone.reset('');
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const country = this.selectedCountry;

    if (!country) return;

    if (this.phoneEntrySupported) {
      const digits = this.extractNationalDigits(
        input.value,
        country
      );

      const formatted = this.formatNationalDigits(
        digits,
        this.parsePhoneGroups(country.phoneNumberFormat)
      );

      this.form.controls.phone.setValue(
        formatted,
        { emitEvent: false }
      );

      return;
    }

    const formatted = this.formatGenericPhone(
      input.value,
      country
    );

    this.form.controls.phone.setValue(
      formatted,
      { emitEvent: false }
    );
  }

  countryDialCode(country: MiniCountryDto): string {
    return country.dialCode?.trim() || `(${country.code})`;
  }

  countryFlag(countryCode: string): string {
    const code = countryCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return '🌐';

    return String.fromCodePoint(
      ...Array.from(code).map(character => 127397 + character.charCodeAt(0))
    );
  }

  submit(): void {
    this.submitted = true;
    this.error = '';
    this.success = '';

    if (this.form.invalid) return;

    const value = this.form.getRawValue();

    if (value.password !== value.confirmPassword) {
      this.error = 'Password confirmation does not match.';
      this.notifyService.showError(this.error, 'Registration');
      return;
    }

    if (this.phoneInvalid) {
      this.error = 'Enter a valid phone number for the selected country or leave it blank.';
      this.notifyService.showError(this.error, 'Registration');
      return;
    }

    const phone = this.buildInternationalPhone(value.phone);

    const payload: RegisterRequest = {
      firstName: value.firstName.trim(),
      lastName: value.lastName.trim(),
      email: value.email.trim().toLowerCase(),
      password: value.password,
      confirmPassword: value.confirmPassword,
      country: value.country.trim().toUpperCase(),
      phone: phone ?? undefined
    };

    this.loading = true;

    this.auth.register(payload).subscribe({
      next: (res: ApiResponse<RegisterResponseData>) => {
        if (res.success) {
          this.success = res?.message || 'Registration successful. Please login.';
          this.tokenStore.setTempToken(res.data.token);

          if (res.data.status === 'New' && res.data.role === 'User') {
            this.router.navigate(['/auth/confirm-pin']);
            return;
          }

          setTimeout(() => this.router.navigateByUrl('/auth/login'), 600);
        } else {
          this.error = res?.message || res?.error?.code || 'Registration failed.';
          this.notifyService.showError(this.error, 'Error');
        }
      },
      error: err => {
        const apiBody = err?.error;
        this.error =
          apiBody?.Message ||
          apiBody?.message ||
          apiBody?.Error?.Details ||
          apiBody?.Error?.Code ||
          apiBody?.error?.details ||
          apiBody?.error?.code ||
          err?.message ||
          'Registration failed.';

        this.notifyService.showError(this.error, 'Error');
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private buildInternationalPhone(value: string): string | null {
    const phone = value.trim();
    const country = this.selectedCountry;

    if (!phone || !country) return null;

    if (this.phoneEntrySupported && country.dialCode) {
      const digits = this.extractNationalDigits(
        phone,
        country
      );

      return digits
        ? `${country.dialCode}${digits}`
        : null;
    }

    const dialCode = country.dialCode?.trim() ?? '';

    if (dialCode) {
      const nationalDigits =
        this.extractGenericNationalDigits(
          phone,
          dialCode
        );

      if (!nationalDigits) return null;

      return `${dialCode}${nationalDigits}`;
    }

    if (!phone.startsWith('+')) return null;

    const digits = phone
      .replace(/\D/g, '')
      .slice(0, 15);

    return digits ? `+${digits}` : null;
  }

  private isValidGenericPhone(
  value: string,
  country: MiniCountryDto
): boolean {
  const dialCode = country.dialCode?.trim() ?? '';

  if (dialCode) {
    const nationalDigits =
      this.extractGenericNationalDigits(
        value,
        dialCode
      );

    const totalDigits =
      dialCode.replace(/\D/g, '').length +
      nationalDigits.length;

    return (
      nationalDigits.length >= 4 &&
      totalDigits >= 7 &&
      totalDigits <= 15
    );
  }

  if (!value.trim().startsWith('+')) {
    return false;
  }

  const digits = value.replace(/\D/g, '');

  return digits.length >= 7 && digits.length <= 15;
}

private extractGenericNationalDigits(
  value: string,
  dialCode: string
): string {
  const trimmed = value.trim();
  let digits = trimmed.replace(/\D/g, '');

  const dialCodeDigits =
    dialCode.replace(/\D/g, '');

  if (
    trimmed.startsWith('+') &&
    dialCodeDigits &&
    digits.startsWith(dialCodeDigits)
  ) {
    digits = digits.slice(dialCodeDigits.length);
  }

  const maximumNationalDigits =
    Math.max(1, 15 - dialCodeDigits.length);

  return digits.slice(0, maximumNationalDigits);
}

private formatGenericPhone(
  value: string,
  country: MiniCountryDto
): string {
  const dialCode = country.dialCode?.trim() ?? '';

  if (dialCode) {
    const digits =
      this.extractGenericNationalDigits(
        value,
        dialCode
      );

    return this.groupGenericDigits(digits);
  }

  const digits = value
    .replace(/\D/g, '')
    .slice(0, 15);

  return digits
    ? `+${this.groupGenericDigits(digits)}`
    : '';
}

private groupGenericDigits(digits: string): string {
  if (!digits) return '';

  const parts: string[] = [];

  for (
    let index = 0;
    index < digits.length;
    index += 3
  ) {
    parts.push(digits.slice(index, index + 3));
  }

  return parts.join(' ');
}

  private formatGenericInternationalPhone(value: string): string {
    const digits = value
      .replace(/\D/g, '')
      .slice(0, 15);

    if (!digits) return '';

    const groups: string[] = [];

    for (let index = 0; index < digits.length; index += 3) {
      groups.push(digits.slice(index, index + 3));
    }

    return `+${groups.join(' ')}`;
  }

  private extractNationalDigits(value: string, country: MiniCountryDto): string {
    let digits = value.replace(/\D/g, '');
    const dialCodeDigits = (country.dialCode ?? '').replace(/\D/g, '');
    const maximum = country.phoneNumberMaxLength ?? 15;

    if (dialCodeDigits && digits.startsWith(dialCodeDigits) && digits.length > maximum) {
      digits = digits.slice(dialCodeDigits.length);
    }

    if (country.removeNationalPrefixZero && digits.startsWith('0')) {
      digits = digits.slice(1);
    }

    return digits.slice(0, maximum);
  }

  private parsePhoneGroups(value?: string | null): number[] {
    return (value ?? '')
      .split(',')
      .map(group => Number(group.trim()))
      .filter(group => Number.isInteger(group) && group > 0);
  }

  private formatNationalDigits(digits: string, groups: number[]): string {
    if (!groups.length) return digits;

    const parts: string[] = [];
    let cursor = 0;

    for (const groupLength of groups) {
      if (cursor >= digits.length) break;
      parts.push(digits.slice(cursor, cursor + groupLength));
      cursor += groupLength;
    }

    if (cursor < digits.length) {
      parts.push(digits.slice(cursor));
    }

    return parts.join(' ');
  }

  private browserCountryCode(): string {
    const locale = navigator.language || '';
    const segments = locale.split('-');
    return segments.length > 1 ? segments[segments.length - 1].toUpperCase() : '';
  }

  get selectedDialCode(): string {
    return this.selectedCountry?.dialCode?.trim() ?? '';
  }

  get phonePlaceholder(): string {
    const country = this.selectedCountry;

    if (country?.phoneNumberPlaceholder?.trim()) {
      return country.phoneNumberPlaceholder.trim();
    }

    return country?.dialCode
      ? '555 123 4567'
      : '+1 555 123 4567';
  }

  get phoneEntrySupported(): boolean {
    const country = this.selectedCountry;

    return !!(
      country?.dialCode &&
      country.phoneNumberMinLength &&
      country.phoneNumberMaxLength &&
      country.phoneNumberFormat
    );
  }

  get phoneInvalid(): boolean {
    const phone = this.form.controls.phone.value.trim();

    if (!phone) return false;

    const country = this.selectedCountry;

    if (!country) return true;

    if (this.phoneEntrySupported) {
      const digits = this.extractNationalDigits(phone, country);
      const minimum = country.phoneNumberMinLength ?? 0;
      const maximum = country.phoneNumberMaxLength ?? 0;

      return digits.length < minimum || digits.length > maximum;
    }

    return !this.isValidGenericPhone(phone, country);
  }

  get phoneInternationalPreview(): string {
    return this.buildInternationalPhone(
      this.form.controls.phone.value
    ) ?? '';
  }
}
